import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, map, Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FilterData } from '../standalones/components/generic-filters-view/filters.interface';
import { Course, NewUser, User, UserInfo } from '../models/user';
import { ErrorInterceptorSkipHeader } from '../common/functions-and-constant/interceptor';
import { ManageCoursesDialogComponent } from '../profiles/manage-courses-dialog/manage-courses-dialog.component';
import { DisableUserDialogComponent } from '../profiles/disable-user-dialog/disable-user-dialog.component';
import { changeFiltersToAPIFormat, FiltersService } from './filters.service';
import { NetworkCacheService } from './network-cache.service';
import { URLService } from './dynamic-url.service';
import { AclService, Feature } from './acl.service';
import { UserService } from './user.service';
import { NotificationToasterService } from './notification-toaster.service';

export interface GetInstitutionUsersResult {
  users: User[];
  total: number;
  totalPages: number;
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class ProfilesService {
  courses = new BehaviorSubject<Course[]>([]); // courses with populated institution
  inviteUser = new BehaviorSubject<boolean>(false);

  private profiledUserSubject = new BehaviorSubject<any>(null);

  // profiles actions helpers
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private refreshUsersSubject = new BehaviorSubject<any>(null);

  profiledUser$ = this.profiledUserSubject.asObservable();
  loading$ = this.loadingSubject.asObservable();
  refreshUsers$ = this.refreshUsersSubject.asObservable();

  user!: User;

  private skipErrorInterceptorOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      [ErrorInterceptorSkipHeader]: '',
    }),
  };

  constructor(
    private http: HttpClient,
    private urlService: URLService,
    private networkCache: NetworkCacheService,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private aclService: AclService,
    private userService: UserService,
    private notificationToaster: NotificationToasterService,
    private filtersService: FiltersService,
  ) {
    this.userService.user
      .pipe(
        untilDestroyed(this),
        map((userData) => userData?.user),
      )
      .subscribe((user) => {
        if (user) {
          this.user = user;
        }
      });
  }

  async getInstitutionUsers(
    page: number = 1,
    limit: number = 500,
    filters: any = null,
    fetchFreshData: boolean = true,
    appliedFilters?: FilterData[],
  ): Promise<GetInstitutionUsersResult> {
    const combinedFilters = {
      ...(filters ? filters : {}),
      ...(appliedFilters?.length ? changeFiltersToAPIFormat(appliedFilters) : {}),
    };

    this.filtersService.logShortSearchQuery(combinedFilters?.search);

    const encodedFilters =
      Object.keys(combinedFilters).length !== 0
        ? btoa(encodeURIComponent(JSON.stringify(combinedFilters)))
        : '';

    const url = `${this.urlService.getDynamicUrl()}/tutor/institution/users?filters=${encodedFilters}&page=${page}&limit=${limit}`;

    if (fetchFreshData) {
      await this.networkCache.clearFilteredUsersCache();
    }

    return firstValueFrom(this.http.get<GetInstitutionUsersResult>(url));
  }

  // Searches users accessible to the current tutor: institution members + session-user connections.
  // Filters are sent on the body to avoid URL length limits when filter values are too large.
  async searchAccessibleUsers(
    page: number = 1,
    limit: number = 100,
    filters: Record<string, any> = {},
  ): Promise<GetInstitutionUsersResult> {
    const url = `${this.urlService.getDynamicUrl()}/tutor/institution/users/search?page=${page}&limit=${limit}`;

    return firstValueFrom(
      this.http.post<GetInstitutionUsersResult>(url, { filters }, this.skipErrorInterceptorOptions),
    );
  }

  postUsers(users: NewUser[], shouldNotify: boolean) {
    return this.http.post(
      `${this.urlService.getDynamicUrl()}/tutor/institution/users`,
      {
        users: users,
        shouldNotify: shouldNotify,
      },
      this.skipErrorInterceptorOptions,
    );
  }

  updateUser(user: User) {
    return this.http.put(`${this.urlService.getDynamicUrl()}/tutor/institution/users/${user._id}`, {
      user,
    });
  }

  updateCurrentUserGeneralInfo(user: User): Observable<any> {
    return this.http.patch(`${this.urlService.getDynamicUrl()}/tutor/institution/users/me`, {
      user,
    });
  }
  getCurrentUserGeneralInfo(): Observable<any> {
    return this.http.get(`${this.urlService.getDynamicUrl()}/tutor/institution/users/me`);
  }

  getUser(id: string): Observable<any> {
    return this.http.get(`${this.urlService.getDynamicUrl()}/tutor/institution/users/${id}`);
  }

  archiveUser(ids: string[], note: string) {
    return this.http.post(`${this.urlService.getDynamicUrl()}/tutor/institution/users/delete`, {
      ids: ids,
      note,
    });
  }

  getAllCourses(): Observable<any> {
    return this.http.get(`${this.urlService.getDynamicUrl()}/tutor/institution/courses`);
  }

  postCourse(course: Course) {
    return this.http.post(`${this.urlService.getDynamicUrl()}/tutor/institution/courses`, {
      course,
    });
  }

  updateCourse(course: Course) {
    return this.http.put(
      `${this.urlService.getDynamicUrl()}/tutor/institution/courses/${course._id}`,
      { course },
    );
  }

  getUsersByCourse(courseId: string) {
    return this.http.get(
      `${this.urlService.getDynamicUrl()}/tutor/institution/users/course/${courseId}`,
    );
  }

  addUsersToCourse(users: string[], courseId): Observable<any> {
    return this.http.post(
      `${this.urlService.getDynamicUrl()}/tutor/institution/users/course/${courseId}`,
      {
        users,
      },
    );
  }

  removeUsersFromCourse(users: string[], courseId): Observable<any> {
    return this.http.post(
      `${this.urlService.getDynamicUrl()}/tutor/institution/users/course/remove/${courseId}`,
      {
        users,
      },
    );
  }

  addUsersToCourses(users: string[], courses: string[]): Observable<any> {
    return this.http.post(`${this.urlService.getDynamicUrl()}/tutor/institution/users/courses`, {
      users,
      courses,
    });
  }

  archiveCourse(courseId: string): Observable<any> {
    return this.http.delete(
      `${this.urlService.getDynamicUrl()}/tutor/institution/courses/${courseId}`,
    );
  }

  refreshCourses(): void {
    this.getAllCourses().subscribe((res) => {
      if (res && res.courses) {
        this.courses.next(res.courses);
      }
    });
  }

  hideRevokedInfo(): Observable<any> {
    return this.http.post(
      `${this.urlService.getDynamicUrl()}/tutor/institution/users/hiderevokedinfo`,
      {},
    );
  }

  updateUserCourses(users: UserInfo[]): Observable<any> {
    return this.http.post(`${this.urlService.getDynamicUrl()}/tutor/institution/users/courses`, {
      users,
    });
  }

  searchInstitution(searchTerm: string): Observable<any> {
    return this.http.get(
      `${this.urlService.getDynamicUrl()}/tutor/institution/search/${searchTerm}`,
    );
  }

  setProfiledUser(user: User | undefined) {
    this.profiledUserSubject.next(user);
  }

  getProfiledUser(): User | null {
    return this.profiledUserSubject.getValue();
  }

  refreshUsers() {
    this.refreshUsersSubject.next(true);
  }

  setLoading(isLoading: boolean) {
    this.loadingSubject.next(isLoading);
  }

  showManageCoursesModal(courses: Course[], selectedUsers: User[]): void {
    const dialogRef = this.dialog.open(ManageCoursesDialogComponent, {
      width: '600px',
      panelClass: 'manage-courses-dialog-container',
      data: {
        courses,
        selectedUsers,
        title: this.translateService.instant('Manage Courses'),
      },
    });

    dialogRef
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe(async (result) => {
        if (result) {
          this.setLoading(true);
          this.updateUserCourses(result)
            .pipe(untilDestroyed(this))
            .subscribe({
              next: () => {
                this.notificationToaster.showDefaultSuccessNotification(
                  this.translateService.instant('Courses successfully updated'),
                  this.notificationToaster.generateNotificationCode(),
                );
                this.refreshUsers();
              },
              error: () => {
                this.setLoading(false);
                this.notificationToaster.showDefaultErrorNotificationV2(
                  this.translateService.instant('Failed to update courses'),
                  '',
                  this.notificationToaster.generateNotificationCode(),
                );
              },
            });
        }
      });
  }

  deleteUser(users: User[]): void {
    if (this.user && !this.aclService.isAllowed(this.user, Feature.delete_user)) {
      return;
    }
    const dialogRef = this.dialog.open(DisableUserDialogComponent, {
      width: '600px',
      panelClass: 'disable-user-dialog-container',
      data: {
        users,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe(async (result) => {
        if (result || result === '') {
          const userIds = users.map((u) => u._id);
          this.setLoading(true);
          this.archiveUser(userIds, result)
            .pipe(untilDestroyed(this))
            .subscribe({
              next: () => {
                this.notificationToaster.showDefaultSuccessNotification(
                  this.translateService.instant('User(s) successfully deleted'),
                  this.notificationToaster.generateNotificationCode(),
                );
                this.refreshUsers();
              },
              error: () => {
                this.setLoading(false);
                this.notificationToaster.showDefaultErrorNotificationV2(
                  this.translateService.instant('Failed to delete user(s)'),
                  '',
                  this.notificationToaster.generateNotificationCode(),
                );
              },
            });
        }
      });
  }
}
